export function applyFilters(jobs, filters, dictionaries) {
  return jobs.filter((job) => {
    if (filters.startMonth && job.m < filters.startMonth) return false;
    if (filters.endMonth && job.m > filters.endMonth) return false;
    if (filters.applicationTypes.length && !filters.applicationTypes.includes(job.a || 'unknown')) return false;
    if (filters.providers.length && !filters.providers.includes(job.pr || 'None')) return false;
    if (filters.hosts.length && !filters.hosts.includes(job.h || '')) return false;
    if (filters.parentCategories.length && !intersects(job.p, filters.parentCategories)) return false;
    if (filters.childTags.length && !intersects(job.t, filters.childTags)) return false;
    if (filters.contracts.length && !intersects(job.ct, filters.contracts)) return false;
    if (filters.levels.length && !filters.levels.includes(job.lv || 'unknown')) return false;
    if (filters.geos.length && !intersects(job.g, filters.geos)) return false;
    if (filters.salaryPresence === 'present' && !job.sal?.present) return false;
    if (filters.salaryPresence === 'parsed' && !job.sal?.parsed) return false;
    if (filters.salaryPresence === 'missing' && job.sal?.present) return false;
    if (filters.salaryCurrencies.length && !filters.salaryCurrencies.includes(job.sal?.currency || '')) return false;
    if (filters.salaryPeriods.length && !filters.salaryPeriods.includes(job.sal?.period || '')) return false;

    return true;
  });
}

export function createDefaultFilters(months) {
  return {
    startMonth: '',
    endMonth: '',
    applicationTypes: [],
    providers: [],
    hosts: [],
    parentCategories: [],
    childTags: [],
    contracts: [],
    levels: [],
    geos: [],
    salaryPresence: 'all',
    salaryCurrencies: [],
    salaryPeriods: []
  };
}

export function monthSeries(jobs, months) {
  const counts = new Map(months.map((month) => [month, 0]));
  for (const job of jobs) {
    if (counts.has(job.m)) {
      counts.set(job.m, counts.get(job.m) + 1);
    }
  }
  return {
    labels: months,
    values: months.map((month) => counts.get(month) || 0)
  };
}

export function dimensionCounts(jobs, key, dictionary = {}) {
  const counts = new Map();
  for (const job of jobs) {
    const values = Array.isArray(job[key]) ? job[key] : [job[key]];
    const unique = new Set(values.filter(Boolean));
    for (const value of unique) {
      counts.set(value, (counts.get(value) || 0) + 1);
    }
  }

  return [...counts.entries()]
    .map(([id, count]) => ({
      id,
      title: dictionary[id]?.title || id || 'Unknown',
      count,
      item: dictionary[id] || null
    }))
    .sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
}

export function companyRows(jobs, dictionaries = {}) {
  const rows = new Map();

  for (const job of jobs) {
    if (!job.c) continue;

    const id = String(job.c);
    const row = rows.get(id) || {
      id,
      title: dictionaries.companies?.[job.c]?.title || `Company ${job.c}`,
      count: 0,
      salaryPresent: 0,
      firstMonth: job.m,
      lastMonth: job.m,
      categoryCounts: new Map(),
      geoCounts: new Map(),
      providerCounts: new Map(),
      sourceCounts: new Map()
    };

    row.count += 1;
    row.salaryPresent += job.sal?.present ? 1 : 0;
    if (job.m && (!row.firstMonth || job.m < row.firstMonth)) row.firstMonth = job.m;
    if (job.m && (!row.lastMonth || job.m > row.lastMonth)) row.lastMonth = job.m;

    addUniqueCounts(row.categoryCounts, job.p);
    addUniqueCounts(row.geoCounts, (job.g || []).filter((id) => id !== 'unknown:unknown'));
    addUniqueCounts(row.providerCounts, job.pr && job.pr !== 'None' ? [job.pr] : []);
    addUniqueCounts(row.sourceCounts, job.s ? [job.s] : []);

    rows.set(id, row);
  }

  return [...rows.values()]
    .map((row) => ({
      id: row.id,
      title: row.title,
      count: row.count,
      share: ratio(row.count, jobs.length),
      firstMonth: row.firstMonth,
      lastMonth: row.lastMonth,
      salaryRate: ratio(row.salaryPresent, row.count),
      topCategory: topDimension(row.categoryCounts, dictionaries.parentCategories),
      topGeo: topDimension(row.geoCounts, dictionaries.geos),
      topProvider: topDimension(row.providerCounts, dictionaries.providers),
      topSource: topDimension(row.sourceCounts, dictionaries.sources)
    }))
    .sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
}

export function growthTable(jobs, key, dictionary = {}, endMonth) {
  const latest = endMonth || maxMonth(jobs);
  if (!latest) return [];

  const currentStart = shiftMonth(latest, -11);
  const previousEnd = shiftMonth(currentStart, -1);
  const previousStart = shiftMonth(previousEnd, -11);

  const currentJobs = jobs.filter((job) => job.m >= currentStart && job.m <= latest);
  const previousJobs = jobs.filter((job) => job.m >= previousStart && job.m <= previousEnd);
  const currentCounts = countDimension(currentJobs, key);
  const previousCounts = countDimension(previousJobs, key);
  const currentTotal = Math.max(currentJobs.length, 1);
  const previousTotal = Math.max(previousJobs.length, 1);
  const ids = new Set([...currentCounts.keys(), ...previousCounts.keys()]);

  return [...ids].map((id) => {
    const current = currentCounts.get(id) || 0;
    const previous = previousCounts.get(id) || 0;
    const delta = current - previous;
    const share = current / currentTotal;
    const previousShare = previous / previousTotal;
    const shareDelta = share - previousShare;
    return {
      id,
      title: dictionary[id]?.title || id || 'Unknown',
      current,
      previous,
      delta,
      share,
      previousShare,
      shareDelta,
      score: Math.abs(delta) + Math.abs(shareDelta * 1000)
    };
  })
    .filter((row) => row.current + row.previous >= 25)
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
}

export function coverageByYear(jobs) {
  const years = new Map();
  for (const job of jobs) {
    const year = job.m.slice(0, 4);
    const row = years.get(year) || {
      year,
      jobs: 0,
      salaryPresent: 0,
      salaryParsed: 0,
      tagsPresent: 0,
      geoPresent: 0,
      providerPresent: 0,
      contractPresent: 0,
      levelPresent: 0
    };
    row.jobs += 1;
    row.salaryPresent += job.sal?.present ? 1 : 0;
    row.salaryParsed += job.sal?.parsed ? 1 : 0;
    row.tagsPresent += job.t?.length ? 1 : 0;
    row.geoPresent += job.g?.some((id) => !id.startsWith('unknown:')) ? 1 : 0;
    row.providerPresent += job.pr && job.pr !== 'None' ? 1 : 0;
    row.contractPresent += job.ct?.length ? 1 : 0;
    row.levelPresent += job.lv && job.lv !== 'unknown' ? 1 : 0;
    years.set(year, row);
  }
  return [...years.values()].sort((a, b) => a.year.localeCompare(b.year));
}

export function salarySummary(jobs) {
  const total = jobs.length;
  const present = jobs.filter((job) => job.sal?.present).length;
  const parsed = jobs.filter((job) => job.sal?.parsed).length;
  const groups = new Map();

  for (const job of jobs) {
    const salary = job.sal;
    if (!salary?.parsed) continue;
    const key = `${salary.currency}-${salary.period}`;
    const group = groups.get(key) || {
      key,
      currency: salary.currency,
      period: salary.period,
      count: 0,
      midpoints: []
    };
    group.count += 1;
    if (Number.isFinite(salary.midpoint)) {
      group.midpoints.push(salary.midpoint);
    }
    groups.set(key, group);
  }

  const segments = [...groups.values()]
    .map((group) => ({
      ...group,
      median: median(group.midpoints)
    }))
    .sort((a, b) => b.count - a.count);

  return {
    total,
    present,
    parsed,
    presentRate: ratio(present, total),
    parsedRate: ratio(parsed, total),
    segments
  };
}

export function selectedPeriodChange(jobs, startMonth, endMonth) {
  if (!startMonth || !endMonth) {
    return { current: jobs.length, previous: 0, delta: jobs.length, percent: null };
  }

  const months = monthDistance(startMonth, endMonth) + 1;
  const previousEnd = shiftMonth(startMonth, -1);
  const previousStart = shiftMonth(previousEnd, -(months - 1));
  const current = jobs.filter((job) => job.m >= startMonth && job.m <= endMonth).length;
  const previous = jobs.filter((job) => job.m >= previousStart && job.m <= previousEnd).length;
  const delta = current - previous;
  const percent = previous > 0 ? delta / previous : null;
  return { current, previous, delta, percent };
}

export function allMonths(jobs) {
  const months = [...new Set(jobs.map((job) => job.m).filter(Boolean))].sort();
  if (!months.length) return [];
  const output = [];
  let cursor = months[0];
  const last = months[months.length - 1];
  while (cursor <= last) {
    output.push(cursor);
    cursor = shiftMonth(cursor, 1);
  }
  return output;
}

export function shiftMonth(month, offset) {
  const [year, monthIndex] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year, monthIndex - 1 + offset, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function countDimension(jobs, key) {
  const counts = new Map();
  for (const job of jobs) {
    const values = Array.isArray(job[key]) ? job[key] : [job[key]];
    for (const value of new Set(values.filter(Boolean))) {
      counts.set(value, (counts.get(value) || 0) + 1);
    }
  }
  return counts;
}

function addUniqueCounts(counts, values = []) {
  for (const value of new Set(values.filter(Boolean))) {
    counts.set(value, (counts.get(value) || 0) + 1);
  }
}

function topDimension(counts, dictionary = {}) {
  const [id, count] = [...counts.entries()].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))[0] || [];
  if (!id) return null;
  return {
    id,
    title: dictionary[id]?.title || id,
    count
  };
}

function maxMonth(jobs) {
  return jobs.reduce((max, job) => (job.m > max ? job.m : max), '');
}

function intersects(values, selected) {
  const set = new Set(values || []);
  return selected.some((value) => set.has(value));
}

function ratio(value, total) {
  return total > 0 ? value / total : 0;
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? Math.round((sorted[middle - 1] + sorted[middle]) / 2)
    : sorted[middle];
}

function monthDistance(startMonth, endMonth) {
  const [startYear, start] = startMonth.split('-').map(Number);
  const [endYear, end] = endMonth.split('-').map(Number);
  return (endYear - startYear) * 12 + (end - start);
}
